import {useState, useMemo, useEffect} from 'react';
import {compareDesc, parseISO} from 'date-fns';
import {
	GetReferralsParams,
	Referrals,
	ReferralTypeEnum,
} from 'domain/usecases/private/my-referrals-flow/get-referrals';
import {useSearchProviders} from 'presentation/pages/private/authorizations/hooks/useSearchProviders';
import {useGetPaginatedItemsReactQuery} from 'presentation/pages/private/hooks/useGetPaginatedItemsReactQuery';
import {useNavigate} from 'react-router-dom';
import {
	AuthorizationsEnum,
	ExamBookingEnum,
	FeaturesEnum,
	HealthBookingEnum,
} from 'presentation/constants';
import {useAuthorizations} from 'presentation/pages/private/authorizations/hooks';
import {getFeatureFlagValue} from 'infra/http/utils/get-feature-flag-value';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {getReferrals} from '../../../services/referrals.services';
import {UseReferralsFlowProps} from './types';

export const useReferralsFlow = (props: UseReferralsFlowProps) => {
	const {searchValueState, debouncedQueryFunc, clearSearchParams, abortDebounce} =
		useSearchProviders();
	const navigate = useNavigate();
	const {isMobile} = useMediaQueries();
	const pageSize = isMobile ? 4 : 6;
	const {
		handleGetAuthSchedule,
		IS_LOADING,
		modalDescriptionState,
		IS_ERROR,
		IS_DONE,
		IS_UNAVAILABLE,
		handleResetOperationState,
	} = useAuthorizations();

	const [getReferralsParamsState, setGetReferralsParamsState] = useState(props.filters);
	const [isSearchEnabled, setIsSearchEnabled] = useState<boolean>(false);

	const [openConfirmationDrawerState, setOpenConfirmationDrawerState] = useState<
		ReferralTypeEnum | 'AUTHORIZATION' | undefined
	>();

	const [selectedReferralCdSenha, setSelectedReferralCdSenha] = useState<string | undefined>();

	const {
		items,
		itemsListIsEmpty,
		itemsListHasError,
		totalItems,
		loadMore,
		resetState,
		refresh,
		isLoading,
		isFetchingNextPage,
	} = useGetPaginatedItemsReactQuery<Referrals>({
		queryKey: [props.queryKey, getReferralsParamsState, searchValueState, pageSize],
		queryFn: async ({page, signal}) => {
			const data = await getReferrals({
				...getReferralsParamsState,
				nomeProcedimento: searchValueState || '',
				page,
				limit: pageSize,
				signal,
			});

			return {
				items: data?.encaminhamentos ?? [],
				total: data?.total ?? 0,
				pages: Math.ceil((data?.total ?? 0) / pageSize),
			};
		},
	});

	const sortedItems = useMemo(
		() =>
			[...items].sort((a, b) =>
				compareDesc(parseISO(a.dtSolicitacao), parseISO(b.dtSolicitacao)),
			),
		[items],
	);

	const handleFilter = (filters: Partial<GetReferralsParams>) => {
		setGetReferralsParamsState({
			...getReferralsParamsState,
			...filters,
		});
	};

	const flowState = useMemo(() => {
		if (isLoading && items?.length === 0) return 'loading';
		if (itemsListIsEmpty) return 'empty';
		if (itemsListHasError) return 'error';
		return 'success';
	}, [isLoading, itemsListIsEmpty, itemsListHasError]);

	const handleOpenDetails = (data: Referrals) => {
		setSelectedReferralCdSenha(data.cdSenha);
	};

	const handleConfirmSchedule = async () => {
		if (openConfirmationDrawerState === ReferralTypeEnum.EXAM) {
			navigate(ExamBookingEnum.EXAM_BOOKING_FLOW);
			return;
		}

		if (openConfirmationDrawerState !== 'AUTHORIZATION') {
			navigate(HealthBookingEnum.HEALTH_BOOKING_FLOW);
			return;
		}

		handleResetOperationState();
		await handleGetAuthSchedule();
	};

	const handleRetryAuthShedule = async () => {
		handleResetOperationState();
		setOpenConfirmationDrawerState('AUTHORIZATION');
		await handleGetAuthSchedule();
	};

	const hideFilter =
		(!getReferralsParamsState.tipoProcedimento ||
			getReferralsParamsState.tipoProcedimento === 'all') &&
		getReferralsParamsState.status === props.filters.status &&
		items.length === 0;

	const showFilterCount =
		(getReferralsParamsState.tipoProcedimento !== 'all' &&
			!!getReferralsParamsState.tipoProcedimento) ||
		getReferralsParamsState.status !== props.filters.status;

	const showSearch =
		isSearchEnabled &&
		!(flowState === 'empty' && searchValueState === '') &&
		flowState !== 'error';

	const isDateFilterActive =
		props.filters.dtFim !== getReferralsParamsState.dtFim ||
		props.filters.dtInicio !== getReferralsParamsState.dtInicio;

	const clearFilters = () => {
		setGetReferralsParamsState(props.filters);
	};
	const loadFeatureFlag = async () => {
		const enabled = await getFeatureFlagValue(
			FeaturesEnum.PORTAL_FEATURE_BUSCA_MEUS_ENCAMINHAMENTOS,
		);
		setIsSearchEnabled(enabled);
	};
	useEffect(() => {
		loadFeatureFlag();
	}, []);

	useEffect(() => {
		if (IS_DONE) {
			navigate(AuthorizationsEnum.REQUEST);
		}
		if (IS_ERROR || IS_UNAVAILABLE) {
			setOpenConfirmationDrawerState(undefined);
		}
	}, [IS_DONE, IS_ERROR, IS_UNAVAILABLE]);

	return {
		flowState,
		handleFilter,
		data: sortedItems,
		isLoading,
		isFetchingNextPage,
		itemsListHasError,
		totalItems,
		getReferralsParamsState,
		handleOpenDetails,
		loadMore,
		resetState,
		refresh,
		showSearch,
		handleConfirmSchedule,
		openConfirmationDrawerState,
		handleRetryAuthShedule,
		setOpenConfirmationDrawerState,
		selectedReferralCdSenha,
		setSelectedReferralCdSenha,
		searchValueState,
		debouncedQueryFunc,
		clearSearchParams,
		abortDebounce,
		IS_LOADING,
		modalDescriptionState,
		IS_ERROR,
		IS_DONE,
		IS_UNAVAILABLE,
		clearFilters,
		handleResetOperationState,
		isSearchEnabled,
		hideFilter,
		isDateFilterActive,
		showFilterCount,
		handleGetAuthSchedule,
		isMobile,
	};
};
